// Adapted from MineRadio d43de565, public/js/modules/05-playback/15-control-glass-animations.js.
// Mineradio, GPL-3.0-only. See docs/third-party/MineRadio-GPL-3.0.txt.
export function supportsControlBackdrop() {
  // WebKit accepts the declarations but can omit the fixed capsule backdrop
  // when it is composited above other fixed surfaces. Syntax support alone
  // must not remove the readable theme surface (also covers iOS browsers).
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent || '';
  if (/AppleWebKit/i.test(ua) && !/Chrome\/|Chromium\/|Edg\//i.test(ua)) return false;
  return typeof CSS !== 'undefined' && ['backdrop-filter', '-webkit-backdrop-filter'].some(property => CSS.supports(property, 'blur(1px)'));
}

export function supportsControlGlassSvgFilter() {
  const ua = navigator.userAgent || '';
  if ((/Safari/.test(ua) && !/Chrome/.test(ua)) || /Firefox/.test(ua)) return false;
  const div = document.createElement('div');
  div.style.backdropFilter = 'url(#mineradio-control-glass-filter)';
  return div.style.backdropFilter !== '';
}

export function generateControlGlassDisplacementMap(width, height, radius) {
  width = Math.max(240, Math.round(width || 400));
  height = Math.max(48, Math.round(height || 92));
  radius = Math.max(12, Math.round(radius || 50));
  const edge = Math.min(width, height) * (0.07 * 0.5);
  const innerW = Math.max(1, width - edge * 2);
  const innerH = Math.max(1, height - edge * 2);
  const svg = '<svg viewBox="0 0 ' + width + ' ' + height + '" xmlns="http://www.w3.org/2000/svg">' +
    '<defs>' +
    '<linearGradient id="glass-red" x1="100%" y1="0%" x2="0%" y2="0%"><stop offset="0%" stop-color="#0000"/><stop offset="100%" stop-color="red"/></linearGradient>' +
    '<linearGradient id="glass-blue" x1="0%" y1="0%" x2="0%" y2="100%"><stop offset="0%" stop-color="#0000"/><stop offset="100%" stop-color="blue"/></linearGradient>' +
    '</defs>' +
    '<rect x="0" y="0" width="' + width + '" height="' + height + '" fill="black"/>' +
    '<rect x="0" y="0" width="' + width + '" height="' + height + '" rx="' + radius + '" fill="url(#glass-red)"/>' +
    '<rect x="0" y="0" width="' + width + '" height="' + height + '" rx="' + radius + '" fill="url(#glass-blue)" style="mix-blend-mode:difference"/>' +
    '<rect x="' + edge.toFixed(2) + '" y="' + edge.toFixed(2) + '" width="' + innerW.toFixed(2) + '" height="' + innerH.toFixed(2) + '" rx="' + radius + '" fill="hsl(0 0% 50% / 1)" style="filter:blur(11px)"/>' +
    '</svg>';
  return 'data:image/svg+xml,' + encodeURIComponent(svg);
}

export function observeControlGlass(bar, image, onReady) {
  if (!supportsControlGlassSvgFilter()) return () => {};
  // MineRadio's default chromatic offset (50/140; base -90, max spread 22).
  const spread = 22 * 50 / 140;
  for (const [channel, delta] of [['Red', -spread], ['Green', 0], ['Blue', spread]]) {
    const node = bar.querySelector(`feOffset[result="disp${channel}Shifted"]`);
    node?.setAttribute('dx', String(Math.round((-90 + delta) * 100) / 100));
    node?.setAttribute('dy', String(Math.round(delta * .08 * 100) / 100));
  }
  let key = '';
  const update = () => {
    const rect = bar.getBoundingClientRect();
    if (rect.width < 2 || rect.height < 2) return;
    const radius = parseFloat(getComputedStyle(bar).borderRadius) || 50;
    const next = `${Math.round(rect.width)}x${Math.round(rect.height)}:${Math.round(radius)}`;
    if (next === key) return;
    key = next;
    image.setAttribute('href', generateControlGlassDisplacementMap(rect.width, rect.height, radius));
    onReady();
  };
  const observer = new ResizeObserver(update);
  observer.observe(bar);
  update();
  return () => observer.disconnect();
}
